import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

type GetAccessToken = () => Promise<string | null>

let getAccessToken: GetAccessToken | null = null

/**
 * Sets the access token getter function that will be used by the interceptor
 * to retrieve the access token for authenticated requests.
 */
export function setAccessTokenGetter(getter: GetAccessToken) {
  getAccessToken = getter
}

/**
 * Attaches request and response interceptors to the axios instance.
 * The request interceptor adds the Authorization header with the Bearer token.
 * The response interceptor handles 401 errors (unauthorized).
 */
export function attachInterceptors(instance: AxiosInstance) {
  // Request interceptor: Add Authorization header with token
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Skip if access token getter is not set
      if (!getAccessToken) {
        return config
      }

      // Skip if Authorization header is already set (allows manual override)
      // Check if the property exists in headers to handle cases where header is explicitly set
      if ('Authorization' in config.headers) {
        return config
      }

      try {
        const token = await getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (error) {
        // If token retrieval fails, continue without token
        // The request will likely fail with 401, which can be handled by the app
        console.warn('Failed to get access token for request:', error)
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  // Response interceptor: Handle 401 errors
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Handle 401 Unauthorized errors
      if (error.response?.status === 401) {
        // Optionally redirect to login or refresh token
        // For now, just reject the error so the calling code can handle it
        console.warn('Request failed with 401 Unauthorized')
      }

      return Promise.reject(error)
    },
  )
}
